import React, { useState, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { GridHelper } from "./GridHelper/GridHelper";
import { StickyBlockContainer } from "../Container/StickyBlockContainer";
import { SectionGridSize } from "../helpers/enums";
import { Scroller, Step } from "../Scroller";
import { useScrollytellState } from "../Admin/scrollytellContext";
import { MultiBackgrounds } from "../background/MultiBackgrounds";
import { SingleBackground } from "../background/SingleBackground";
import { BackgroundContainer } from "../background/BackgroundContainer";
import { Blocks } from "../Block/Blocks";
import { Container } from "../Container/Container";
import { EditLockContainer } from "../Container/EditLockContainer";
import { EditLockBlock } from "../lock/EditLockBlock";
import { LockBlock } from "../lock/LockBlock";
import { LockContainer } from "../lock/LockContainer";
import { useContentHeight } from "../map/useContentHeight";
import { nineColumnsGrid, twelveColumnsGrid, thumbnailGrid } from "./sectionGridStyles";
import { normalizeMultiBackgrounds } from "./stickySectionHelpers";

const useStyles = createUseStyles({
  grid: {
    top: 0,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    position: "relative",
    minHeight: ({ minHeight }) => minHeight,
    height: ({ height }) => height,
    display: "grid",
    zIndex: 10,
  },
  thumbnail: thumbnailGrid,
  nineColumns: nineColumnsGrid,
  twelveColumns: twelveColumnsGrid,
});

export const StickySection = ({ sectionId, isAdminMode, section, isThumbnail }) => {
  const {
    backgroundColor,
    gradient,
    height,
    backgroundImage,
    backgroundVideo,
    containers,
    backgroundFocus,
    gridSize = SectionGridSize.NINE_COLUMNS,
    lock,
  } = section;
  const { showGrid, activeElement } = useScrollytellState();
  const contentHeight = useContentHeight();
  const minHeight = `${contentHeight}px`;
  const classes = useStyles({ height, minHeight, gridSize });
  const [currentStep, setCurrentStep] = useState(null);

  const activeContainerIdx =
    isAdminMode && activeElement?.[0] === sectionId && typeof activeElement?.[1] === "number"
      ? activeElement[1]
      : null;

  useEffect(() => {
    if (activeContainerIdx !== null) {
      setCurrentStep(activeContainerIdx);
    }
  }, [activeContainerIdx]);

  const onStepEnter = ({ data: { i } }) => setCurrentStep(i);
  const onStepLeave = ({ data: { i }, direction }) => {
    if (Array.isArray(backgroundImage)) {
      if (i === 0 && direction === "up") {
        setCurrentStep(0);
      }
      return;
    }
    setCurrentStep((prev) => (prev === i ? null : prev));
  };

  const renderBackground = () => {
    if (typeof backgroundImage === "string" || backgroundVideo) {
      const activeContainer =
        currentStep !== null && containers?.[currentStep] ? containers[currentStep] : null;
      const activeStepKey = currentStep !== null ? `step-${currentStep}` : "overview";
      return (
        <SingleBackground
          gradient={gradient}
          isAdminMode={isAdminMode}
          backgroundImage={backgroundImage}
          backgroundVideo={backgroundVideo}
          height={height}
          backgroundColor={backgroundColor}
          minHeight={minHeight}
          backgroundFocus={backgroundFocus}
          kenBurns={activeContainer?.kenBurns || backgroundFocus}
          activeStepKey={activeStepKey}
          priority={sectionId === 0}
        />
      );
    }
    if (Array.isArray(backgroundImage)) {
      return (
        <MultiBackgrounds
          backgroundImage={normalizeMultiBackgrounds(section)}
          currentStep={currentStep}
          gradient={gradient}
          contentHeight={contentHeight}
          priority={sectionId === 0}
          isAdminMode={isAdminMode}
        />
      );
    }
    return null;
  };

  const hasLock = Boolean(containers?.find((c) => c.blocks?.find((b) => b.locked === true)));

  return (
    <>
      {showGrid && isAdminMode && (
        <GridHelper sectionId={sectionId} gridSize={gridSize} backgroundColor={backgroundColor} />
      )}
      <article className={`${classes.grid} ${classes[gridSize]} ${isThumbnail ? classes.thumbnail : ""}`}>
        <Scroller
          onStepEnter={onStepEnter}
          onStepLeave={onStepLeave}
          section={section}
          containersCount={containers.length}
          isThumbnail={isThumbnail}
          offset={0.65}
          contentHeight={contentHeight}
        >
          {containers?.slice(0, isThumbnail ? 1 : containers?.length).map((container, i) => (
            <Step data={{ container, i }} key={i}>
              <StickyBlockContainer
                key={`container-${i}`}
                sectionId={sectionId}
                containerId={i}
                isAdminMode={isAdminMode}
                container={container}
                gridSize={gridSize}
                isThumbnail={isThumbnail}
                hasBlocks={Boolean(container.blocks?.length)}
              >
                <Blocks sectionId={sectionId} containerId={i} isAdminMode={isAdminMode} blocks={container.blocks} />
              </StickyBlockContainer>
            </Step>
          ))}
        </Scroller>
        <LockContainer hasLock={hasLock}>
          <Container
            key="lock"
            sectionId={sectionId}
            containerId="lock"
            isAdminMode={isAdminMode}
            gridSize={gridSize}
            backgroundColor={lock?.backgroundColor}
            startColumn={lock?.startColumn}
            endColumn={lock?.endColumn}
            horizontalAlign={lock?.horizontalAlign}
            verticalAlign={lock?.verticalAlign}
            borderRadius={lock?.borderRadius}
            isThumbnail={isThumbnail}
            hasBlocks={true}
          >
            <EditLockContainer sectionId={sectionId} containerId="lock" isAdminMode={isAdminMode} gridSize={gridSize} container={lock} />
            <EditLockBlock lock={lock} sectionId={sectionId} isAdminMode={isAdminMode}>
              <LockBlock lock={lock} backgroundColor={lock?.backgroundColor} sectionId={sectionId} />
            </EditLockBlock>
          </Container>
        </LockContainer>
      </article>
      <BackgroundContainer>{renderBackground()}</BackgroundContainer>
    </>
  );
};
